import React, {
  ForwardedRef,
  forwardRef,
  HTMLAttributes,
  createContext,
  useContext,
} from 'react';
import clsx from 'clsx';
import { filterDOMProps, mergeProps } from '@react-aria/utils';
import { DOMProps } from '@react-types/shared';
import fieldStyles from '@danske/sapphire-css/components/field/field.module.css';
import textStyles from '@danske/sapphire-css/components/text/text.module.css';

import { SapphireStyleProps, useSapphireStyleProps } from '../../utils';
import { GlobalDomAttributes } from '../../utils/types';
import { Icon } from '../../Icon';
import { Error, Warning, CheckmarkOutline } from '@danske/sapphire-icons/react';
export interface FieldProps
  extends GlobalDomAttributes,
    SapphireStyleProps,
    DOMProps {
  /**
   * @default 'large'
   */
  size?: 'lg' | 'md';
  isDisabled?: boolean;
  noShrink?: boolean;
  /**
   * Sets wrapping element type
   * @default 'div'
   */
  elementType?: 'div' | 'fieldset';
  children?: React.ReactNode;
  /**
   * @default 'above'
   */
  labelPlacement?: 'side' | 'above';
  /**
   * Controls like RadioGroup require label to be aligned with the first item,
   * while it's center aligned for TextFields.
   * Only relevant for  labelPlacement="side"
   * @default 'center'
   */
  labelVerticalAlignment?: 'center' | 'top';
  /**
   * Opt out of the default width
   * @default false
   */
  noDefaultWidth?: boolean;
}

interface FieldContextType {
  descriptionProps?: HTMLAttributes<HTMLElement>;
}

const FieldContext = createContext<FieldContextType>({});

export const FieldContextProvider = ({
  children,
  descriptionProps,
}: {
  children: React.ReactNode;
  descriptionProps?: HTMLAttributes<HTMLElement>;
}): React.JSX.Element => (
  <FieldContext.Provider value={{ descriptionProps }}>
    {children}
  </FieldContext.Provider>
);

export const Field = forwardRef(function Field(
  {
    size = 'lg',
    isDisabled = false,
    noShrink = false,
    noDefaultWidth = false,
    elementType: CustomElement = 'div',
    children,
    labelPlacement = 'above',
    labelVerticalAlignment = 'center',
    ...props
  }: FieldProps,
  ref: ForwardedRef<HTMLDivElement | HTMLFieldSetElement>
) {
  const { styleProps } = useSapphireStyleProps(props);

  return (
    <CustomElement
      // @ts-expect-error Cannot type so that TS knows when 'CustomElement'
      // matches the 'ref' type. Type overloading does not seem to help in this
      // case.
      ref={ref}
      className={clsx(
        fieldStyles['sapphire-field'],
        {
          [fieldStyles['is-disabled']]: isDisabled,
          [fieldStyles['sapphire-field--md']]: size === 'md',
          [fieldStyles['sapphire-field--no-shrink']]: noShrink,
          [fieldStyles['sapphire-field--no-width']]:
            noDefaultWidth ||
            // see `.sapphire-field--label-placement-side` css
            // TODO(v5): fields with side labels should not get default widths (UC-4341)
            (labelPlacement === 'side' &&
              (props.width || props.maxWidth || props.minWidth)),
          [fieldStyles['sapphire-field--label-placement-side']]:
            labelPlacement === 'side',
          [fieldStyles['sapphire-field--label-alignment-top']]:
            labelPlacement === 'side' && labelVerticalAlignment === 'top',
        },
        styleProps.className
      )}
      {...(isDisabled && CustomElement === 'fieldset'
        ? { disabled: true }
        : {})}
      style={styleProps.style}
      {...filterDOMProps(props, { labelable: true, global: true })}
    >
      {children}
    </CustomElement>
  );
});

export const FieldControl = ({
  children,
  isGroup = false,
}: {
  children?: React.ReactNode;
  isGroup?: boolean;
}): React.JSX.Element => (
  <div
    className={clsx(fieldStyles['sapphire-field__control'], {
      [fieldStyles['sapphire-field__control--group']]: isGroup,
    })}
  >
    {children}
  </div>
);

export const FieldLabel = ({
  elementType: CustomElement = 'div',
  children,
}: {
  /**
   * Sets wrapping element type
   * @default 'div'
   */
  elementType?: 'div' | 'legend';
  children?: React.ReactNode;
}): React.JSX.Element => (
  <CustomElement className={clsx(fieldStyles['sapphire-field__label'])}>
    {children}
  </CustomElement>
);

export const FieldFooter = ({
  children,
}: {
  children?: React.ReactNode;
}): React.JSX.Element => (
  <div className={clsx(fieldStyles['sapphire-field__message'])}>
    <div className={clsx(fieldStyles['sapphire-field__note-row'])}>
      {children}
    </div>
  </div>
);

const getIcon = (variant: string) => {
  switch (variant) {
    case 'warning':
      return <Warning />;
    case 'success':
      return <CheckmarkOutline />;
    case 'error':
      return <Error />;
    default:
      return null;
  }
};

const getIconColor = (variant: string) => {
  switch (variant) {
    case 'warning':
      return 'warning';
    case 'success':
      return 'positive';
    case 'error':
      return 'negative';
    default:
      return 'secondary';
  }
};

export const FieldNote = ({
  children,
  variant = 'neutral',
  ...props
}: {
  children?: React.ReactNode;
  variant?: 'warning' | 'neutral' | 'success' | 'error';
} & HTMLAttributes<HTMLElement>): React.JSX.Element => {
  const { descriptionProps } = useContext(FieldContext);

  return (
    <>
      {variant !== 'neutral' && (
        <span className={fieldStyles['sapphire-field__note-icon']}>
          <Icon size="sm" color={getIconColor(variant)}>
            {getIcon(variant)}
          </Icon>
        </span>
      )}
      <div
        className={clsx(fieldStyles['sapphire-field__note'], {
          [textStyles['sapphire-text--warning']]: variant === 'warning',
          [textStyles['sapphire-text--positive']]: variant === 'success',
          [textStyles['sapphire-text--negative']]: variant === 'error',
        })}
        {...mergeProps(descriptionProps, props)}
      >
        {children}
      </div>
    </>
  );
};
